// Stripe card fields right on the checkout, for EUR orders. A Fulfillio payment only exists
// once the order does and its amount can't change, so the fields load without one: the
// buyer fills them in, then pressing Pay places the order, creates the payment and charges.
//
// A failed charge stays on the checkout, which retries the same order. Once it goes through,
// /placanje/:orderId waits for the webhook — as everywhere else, only that marks it paid.

import React from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe, stripeLocale, paymentReturnUrl } from './cardPayment';

// Country and zip come from the checkout's own fields (billing_details in pay), wallets stay
// off so this is only ever a card form.
const CARD_FIELD_OPTIONS = {
  fields: { billingDetails: { address: { country: 'never', postalCode: 'never' } } },
  wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
};

const CardFields = React.forwardRef(function CardFields({ apiUrl }, ref) {
  const stripe = useStripe();
  const elements = useElements();

  React.useImperativeHandle(ref, () => ({
    // False if the card fields are incomplete — Stripe marks them itself, and no order is placed.
    validate: async () => {
      if (!elements) return true;
      const { error } = await elements.submit();
      return error?.type !== 'validation_error';
    },

    // Charge a placed order: { next } is where to send the buyer once it went through,
    // { error } is what to show them on the checkout so they can try again.
    pay: async (orderId, billingDetails) => {
      const page = `/placanje/${encodeURIComponent(orderId)}`;
      const unavailable = 'Card payment is unavailable right now. Please try again later or contact us on Instagram.';
      if (!stripe || !elements) return { error: unavailable };

      try {
        const { error: cardError, paymentMethod } = await stripe.createPaymentMethod({
          elements,
          params: { billing_details: billingDetails },
        });
        if (cardError) return { error: cardError.message };

        const res = await fetch(`${apiUrl}/api/payments/${encodeURIComponent(orderId)}/intent`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
        });
        const data = await res.json();
        if (!res.ok || !data.clientSecret) return { error: unavailable };

        const { error, paymentIntent } = await stripe.confirmPayment({
          clientSecret: data.clientSecret,
          confirmParams: {
            return_url: paymentReturnUrl(orderId),
            payment_method: paymentMethod.id,
          },
          redirect: 'if_required',
        });

        if (paymentIntent && ['succeeded', 'processing'].includes(paymentIntent.status)) {
          return { next: `${page}?redirect_status=succeeded` };
        }

        console.error('Inline card payment failed:', error);
        return { error: error?.message || 'Payment failed. Please try again.' };
      } catch (err) {
        console.error('Inline card payment failed:', err);
        return { error: err?.message || unavailable };
      }
    },
  }));

  return <PaymentElement options={CARD_FIELD_OPTIONS} />;
});

const InlineCardPayment = React.forwardRef(function InlineCardPayment({ apiUrl, publishableKey, amount, language }, ref) {
  return (
    <Elements
      stripe={getStripe(publishableKey)}
      options={{
        mode: 'payment',
        amount: Math.round(Number(amount) * 100),
        currency: 'eur',
        // Cards only, though Fulfillio's payment allows more. Stripe won't confirm that payment
        // from Elements limited this way, so pay creates the card first and confirms with it.
        paymentMethodTypes: ['card'],
        paymentMethodCreation: 'manual',
        locale: stripeLocale(language),
        appearance: {
          theme: 'stripe',
          labels: 'floating',
          variables: { colorPrimary: '#A56C3B', colorText: '#111827', borderRadius: '8px', fontSizeBase: '15px' },
        },
      }}
    >
      <CardFields ref={ref} apiUrl={apiUrl} />
    </Elements>
  );
});

export default InlineCardPayment;
